import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, RefreshCw, Phone, CheckCircle2, XCircle, Loader2, Zap, Package } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  useOutboundQueue,
  useOutboundStatsFromDB,
  useOutboundBatches,
  type OutboundStatus,
  type BatchFilter,
} from '@/hooks/useOutboundQueue';
import { useOutboundActions } from '@/hooks/useOutboundActions';
import { useOutboundAutoTopup } from '@/hooks/useOutboundAutoTopup';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useUserRole } from '@/hooks/useUserRole';
import { OutboundContactCard } from '@/components/outbound/OutboundContactCard';
import { PowerDialerBar } from '@/components/outbound/PowerDialerBar';


type FilterStatus = 'all' | OutboundStatus;

export default function OutboundPanel() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [batchFilter, setBatchFilter] = useState<BatchFilter>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentAppUser();
  const { role } = useUserRole();
  const { data: queue = [], isLoading, isFetching } = useOutboundQueue(batchFilter);
  const { updateStatus, updateNotes } = useOutboundActions();
  const { data: stats = { pending: 0, noAnswer: 0, notInterested: 0, total: 0 } } =
    useOutboundStatsFromDB(batchFilter);
  const { data: batchInfo = { batches: [], hasNormal: false } } = useOutboundBatches();
  // Pendentes totais do SDR (todos os lotes) — se ficar em 200 ou menos, completa só a fila dele.
  const { data: allStats } = useOutboundStatsFromDB('all');
  useOutboundAutoTopup(currentUser?.id, allStats?.pending);

  const isManager = role === 'admin' || role === 'manager' || role === 'sales_director' || role === 'sales_manager';

  // Persist batch selection per user; default to newest batch on first load
  useEffect(() => {
    if (!currentUser?.id) return;
    const storageKey = `outbound-batch-filter-${currentUser.id}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setBatchFilter(saved as BatchFilter);
      return;
    }
    // First time on this device: if SDR has any batch, default to the newest (sorted last)
    if (batchInfo.batches.length > 0) {
      const newest = batchInfo.batches[batchInfo.batches.length - 1];
      setBatchFilter(newest);
      localStorage.setItem(storageKey, newest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, batchInfo.batches.length]);

  const handleBatchChange = (v: string) => {
    setBatchFilter(v as BatchFilter);
    if (currentUser?.id) {
      localStorage.setItem(`outbound-batch-filter-${currentUser.id}`, v);
    }
  };

  const refreshAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['outbound-queue'] }),
      queryClient.invalidateQueries({ queryKey: ['outbound-stats-db'] }),
      queryClient.invalidateQueries({ queryKey: ['outbound-batches'] }),
    ]);
  };

  const handleForceAssign = async () => {
    setIsAssigning(true);
    try {
      const { error } = await supabase.functions.invoke('outbound-topup');
      if (error) throw error;
      await refreshAll();
      toast.success('Contatos atribuídos com sucesso!');
    } catch (err) {
      console.error('[Outbound] Force assign error:', err);
      toast.error('Erro ao atribuir contatos');
    } finally {
      setIsAssigning(false);
    }
  };

  const filtered = useMemo(() => {
    // "Todos" representa a fila ativa. Resultados concluídos permanecem
    // consultáveis no filtro específico, mas não voltam ao fluxo de discagem.
    let list = statusFilter === 'all'
      ? queue.filter((c) => c.status === 'pending')
      : queue;

    if (statusFilter !== 'all') {
      list = list.filter((c) => c.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => {
        const name = `${c.contact.first_name ?? ''} ${c.contact.last_name ?? ''}`.toLowerCase();
        const phone = c.contact.primary_phone ?? '';
        const email = c.contact.primary_email ?? '';
        return name.includes(q) || phone.includes(q) || email.includes(q);
      });
    }

    // Sort: pending first, then by assigned_at
    return [...list].sort((a, b) => {
      const order: Record<OutboundStatus, number> = {
        pending: 0,
        called_no_answer: 1,
        called_not_interested: 2,
        deal_created: 3,
      };
      if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
      return new Date(a.assigned_at).getTime() - new Date(b.assigned_at).getTime();
    });
  }, [queue, statusFilter, search]);

  const handleStatusChange = async (
    assignmentId: string,
    sdrId: string,
    status: OutboundStatus,
    incrementAttempts?: boolean
  ) => {
    setUpdatingId(assignmentId);
    try {
      await updateStatus.mutateAsync({ assignmentId, sdrId, status, incrementAttempts });
    } catch {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleNoteSave = async (assignmentId: string, sdrId: string, notes: string) => {
    try {
      await updateNotes.mutateAsync({ assignmentId, notes, sdrId });
    } catch {
      toast.error('Erro ao salvar nota');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold">Painel Outbound</h1>
            {currentUser && (
              <p className="text-sm text-muted-foreground">{currentUser.display_name}</p>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 flex-wrap">
            <StatBadge
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Pendentes"
              value={stats.pending}
              color="blue"
            />
            <StatBadge
              icon={<Phone className="h-3.5 w-3.5 text-yellow-600" />}
              label="Sem resposta"
              value={stats.noAnswer}
              color="yellow"
            />
            <StatBadge
              icon={<XCircle className="h-3.5 w-3.5 text-red-500" />}
              label="Não quis"
              value={stats.notInterested}
              color="red"
            />
            <StatBadge
              icon={<CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
              label="Total na fila"
              value={stats.total}
              color="green"
            />

            {isManager && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleForceAssign}
                disabled={isAssigning || isFetching}
                className="h-8 gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50"
                title="Atribuir contatos agora para todos os SDRs outbound"
              >
                {isAssigning ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                Atribuir agora
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              onClick={() => refreshAll()}
              disabled={isFetching}
              className="h-8"
            >
              {isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, telefone ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FilterStatus)}>
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="called_no_answer">Sem resposta</SelectItem>
              <SelectItem value="called_not_interested">Não quis</SelectItem>
            </SelectContent>
          </Select>

          {(batchInfo.batches.length > 0 || batchInfo.hasNormal) && (
            <Select value={batchFilter} onValueChange={handleBatchChange}>
              <SelectTrigger className="h-9 w-[200px] gap-1.5">
                <Package className="h-3.5 w-3.5 opacity-60" />
                <SelectValue placeholder="Lote" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os lotes</SelectItem>
                {batchInfo.hasNormal && (
                  <SelectItem value="normal">Fila normal</SelectItem>
                )}
                {batchInfo.batches.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Power Dialer */}
        <div className="mt-4">
          <PowerDialerBar queue={filtered} batchLabel={batchFilter === 'all' || batchFilter === 'normal' ? null : batchFilter} />
        </div>
      </div>


      {/* Contact list */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center text-muted-foreground">
            <Phone className="h-10 w-10 mb-3 opacity-30" />
            {filtered.length === 0 && statusFilter === 'all' && !search ? (
              <>
                <p className="font-medium">Fila vazia</p>
                <p className="text-sm">Não há contatos pendentes para discar.</p>
              </>
            ) : (
              <>
                <p className="font-medium">Nenhum resultado</p>
                <p className="text-sm">Tente mudar os filtros.</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 max-w-3xl mx-auto">
            <p className="text-xs text-muted-foreground mb-1">
              {filtered.length} contato{filtered.length !== 1 ? 's' : ''}
              {statusFilter !== 'all' || search ? ' (filtrados)' : ' na fila'}
            </p>
            {filtered.map((item) => (
              <OutboundContactCard
                key={item.id}
                item={item}
                onStatusChange={handleStatusChange}
                onNoteSave={handleNoteSave}
                isUpdating={updatingId === item.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'blue' | 'yellow' | 'red' | 'green';
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
  };
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border ${colors[color]}`}>
      {icon}
      <span>{value}</span>
      <span className="text-[10px] opacity-70">{label}</span>
    </div>
  );
}
